import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { groupBy, isEmpty, propOr } from 'ramda';
import { useQuery } from '@apollo/react-hooks';
import { GET_CANDIDATES_CHART, GET_VISITS_DATA, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { Page, Card, AppProvider, Link, Frame, Stack, Select, EmptyState, Icon } from '@shopify/polaris';
import { TitleBar } from '@shopify/app-bridge-react';
import { LockMinor } from '@shopify/polaris-icons';
import Loader from '../components/SkeletonLoader';
import { Pie, Line } from 'react-chartjs-2';
import randomColor from 'randomcolor';
import Footer from './Footer';

const dateOptions = [
  { label: 'All', value: 'all' },
  { label: 'Today', value: '1' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 14 days', value: '14' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Last year', value: '365' },
];

const ChartEmptyState = (props = {}) => (
  <EmptyState heading='No data found'>
    <p>{`There were no ${props.term || 'data'} in this date range.`}</p>
  </EmptyState>
);

const Analytics = ({ history, shopifyToken }) => {
  const { push: changeRoute } = history;

  const { data: candidatesData = {}, loading: loadingCandidates } = useQuery(GET_CANDIDATES_CHART, {
    variables: { shopifyToken },
  });
  const { data: visitsData = {}, loading: loadingVisits } = useQuery(GET_VISITS_DATA, {
    variables: { shopifyToken },
  });
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, { variables: { shopifyToken } });
  const loading = loadingCandidates || loadingVisits;
  const { candidates = [] } = { ...candidatesData };
  const { shop: visitsHolder } = { ...visitsData };
  const { visits = [], subscribers = [] } = { ...visitsHolder };
  const [overTimeData, setOverTimeData] = useState();
  const [deviceData, setDeviceData] = useState();
  const [sourceData, setSourceData] = useState();
  const [dispositionCodesData, setDispositionCodesData] = useState();
  const [tagsData, setTagsData] = useState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [dateRange, setDateRange] = useState('all');

  const shop = propOr({}, 'shop')(subscriptionData);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const isSubscribedUser = !isEmpty(currentSubscription);

  const handleDateRangeChange = useCallback((newValue) => {
    setDateRange(newValue);
    if (newValue === 'all') {
      setStartDate();
    } else {
      setStartDate(moment(new Date()).subtract(Number(newValue), 'days'));
    }
  }, []);

  const filterCandidates = () => {
    let selectedCandidates = candidates;
    selectedCandidates = selectedCandidates.filter(({ createdAt }) => {
      return moment(createdAt).isBetween(moment(startDate || '01/01/1970'), moment(endDate || new Date()));
    });
    return selectedCandidates;
  };

  const filterVisits = () => {
    let selectedVisits = visits;
    selectedVisits = selectedVisits.filter(({ startedAt }) => {
      return moment(startedAt).isBetween(moment(startDate || '01/01/1970'), moment(endDate || new Date()));
    });
    return selectedVisits;
  };

  const socialMediaClicks = () => {
    const visits = filterVisits();
    const events = (visits || []).map(({ events = [] }) => events).reduce((a, b) => [...a, ...b], []);
    return (events || []).filter(({ name }) => name === 'Social Media Clicked').length;
  };

  const filterCandidatesNewsletters = () => {
    let selectedCandidates = candidates;
    selectedCandidates = selectedCandidates.filter(({ createdAt, subscribe }) => {
      return moment(createdAt).isBetween(moment(startDate || '01/01/1970'), moment(endDate || new Date())) && !!subscribe;
    });
    return selectedCandidates;
  };

  const filterCandidatesNewslettersPercentage = () => {
    const total = (filterCandidates() || []).length;
    const newsletter = (filterCandidatesNewsletters() || []).length;
    if (total > 0 && newsletter > 0) {
      return Math.round((newsletter * 100) / total);
    }
    return 0;
  };

  const filterSubscribers = () => {
    let selectedSubscribers = subscribers;
    selectedSubscribers = selectedSubscribers.filter(({ createdAt }) => {
      return moment(createdAt).isBetween(moment(startDate || '01/01/1970'), moment(endDate || new Date()));
    });
    return selectedSubscribers;
  };

  const careerPortalVisits = () => {
    let selectedVisits = filterVisits();
    selectedVisits = selectedVisits.filter(({ landingPage }) => {
      return landingPage.indexOf('/jobs/') === -1;
    });
    return selectedVisits;
  };

  const jobDetailsVisits = () => {
    let selectedVisits = filterVisits();
    selectedVisits = selectedVisits.filter(({ landingPage }) => {
      return landingPage.indexOf('/jobs/') !== -1;
    });
    return selectedVisits;
  };

  const funnelConversion = () => {
    const totalVisits = jobDetailsVisits().length;
    const totalCandidates = filterCandidates().length;

    if (totalVisits === 0 || totalCandidates === 0) {
      return 0;
    }

    return Math.round((totalCandidates / totalVisits) * 100);
  };

  const buildDeviceData = () => {
    const selectedCandidates = filterCandidates();
    const selectedDevices = Array.from(
      new Set(
        selectedCandidates.map(({ visitorData = {} }) => {
          const { device = 'other' } = { ...visitorData };
          return !!device ? device.charAt(0).toUpperCase() + device.slice(1) : 'Other';
        })
      )
    );
    const rgbColors = selectedDevices.map(() => randomColor({ format: 'rgbArray' }));
    setDeviceData({
      labels: selectedDevices,
      datasets: [
        {
          label: '# of Candidates',
          data: selectedDevices.map((el) => {
            return selectedCandidates.filter(({ visitorData = {} }) => {
              const { device = 'Other' } = { ...visitorData };
              return (!!device ? device.charAt(0).toUpperCase() + device.slice(1) : 'Other') === el;
            }).length;
          }),
          backgroundColor: rgbColors.map(([r, g, b]) => `rgba(${r}, ${g}, ${b}, 0.2)`),
          borderColor: rgbColors.map(([r, g, b]) => `rgba(${r}, ${g}, ${b}, 1)`),
          borderWidth: 1,
        },
      ],
      isEmpty: isEmpty(selectedCandidates),
    });
  };

  const buildSourceData = () => {
    const selectedCandidates = filterCandidates();
    const selectedSources = Array.from(
      new Set(
        selectedCandidates.map(({ source = 'Other' }) => (!!source ? source.charAt(0).toUpperCase() + source.slice(1) : 'Other'))
      )
    );
    const rgbColors = selectedSources.map(() => randomColor({ format: 'rgbArray' }));
    setSourceData({
      labels: selectedSources,
      datasets: [
        {
          label: '# of Candidates',
          data: selectedSources.map((el) => {
            return selectedCandidates.filter(
              ({ source = 'Other' }) => (!!source ? source.charAt(0).toUpperCase() + source.slice(1) : 'Other') === el
            ).length;
          }),
          backgroundColor: rgbColors.map(([r, g, b]) => `rgba(${r}, ${g}, ${b}, 0.2)`),
          borderColor: rgbColors.map(([r, g, b]) => `rgba(${r}, ${g}, ${b}, 1)`),
          borderWidth: 1,
        },
      ],
      isEmpty: isEmpty(selectedCandidates),
    });
  };

  const buildDispositionCodeData = () => {
    const selectedCandidates = filterCandidates().filter(({ status }) => status === 'inactive');
    const selectedCodes = Array.from(
      new Set(
        selectedCandidates.map(({ dispositionCode = {} }) =>
          !!dispositionCode && !!dispositionCode.name ? dispositionCode.name : 'None'
        )
      )
    );
    const rgbColors = selectedCodes.map(() => randomColor({ format: 'rgbArray' }));
    setDispositionCodesData({
      labels: selectedCodes,
      datasets: [
        {
          label: '# of Candidates',
          data: selectedCodes.map((el) => {
            return selectedCandidates.filter(
              ({ dispositionCode = {} }) => (!!dispositionCode && !!dispositionCode.name ? dispositionCode.name : 'None') === el
            ).length;
          }),
          backgroundColor: rgbColors.map(([r, g, b]) => `rgba(${r}, ${g}, ${b}, 0.2)`),
          borderColor: rgbColors.map(([r, g, b]) => `rgba(${r}, ${g}, ${b}, 1)`),
          borderWidth: 1,
        },
      ],
      isEmpty: isEmpty(selectedCandidates),
    });
  };

  const buildTagsData = () => {
    const selectedCandidates = filterCandidates().filter(({ status }) => status === 'inactive');
    const selectedTags = Array.from(
      new Set(
        selectedCandidates
          .map(({ tags = {} }) => (!!tags ? tags.map(({ name }) => name) : ['None']))
          .reduce((a, b) => [...a, ...b], [])
      )
    );
    const rgbColors = selectedTags.map(() => randomColor({ format: 'rgbArray' }));
    setTagsData({
      labels: selectedTags.map((tag) => tag.charAt(0).toUpperCase() + tag.slice(1)),
      datasets: [
        {
          label: '# of Candidates',
          data: selectedTags.map((el) => {
            return selectedCandidates.filter(({ tags = [] }) => tags.map(({ name }) => name).indexOf(el) !== -1).length;
          }),
          backgroundColor: rgbColors.map(([r, g, b]) => `rgba(${r}, ${g}, ${b}, 0.2)`),
          borderColor: rgbColors.map(([r, g, b]) => `rgba(${r}, ${g}, ${b}, 1)`),
          borderWidth: 1,
        },
      ],
      isEmpty: isEmpty(selectedCandidates),
    });
  };

  const buildOverTimeData = () => {
    const selectedCandidates = filterCandidates();

    let selectedDates = Array.from(new Set(selectedCandidates.map(({ createdAt }) => createdAt))).sort(
      (a, b) => moment(a).format('YYYYMMDD') - moment(b).format('YYYYMMDD')
    );

    let jobViews = jobDetailsVisits();
    jobViews = groupBy(({ startedAt }) => moment(startedAt).format('YYYYMMDD'), jobViews);
    const jobViewsData = Object.keys(jobViews)
      .sort((a, b) => moment(a).format('YYYYMMDD') - moment(b).format('YYYYMMDD'))
      .map((date) => ({
        y: jobViews[date].length,
        x: moment(date).format('MM/DD/YYYY'),
      }));

    let careerPortalViews = careerPortalVisits();
    careerPortalViews = groupBy(({ startedAt }) => moment(startedAt).format('YYYYMMDD'), careerPortalViews);
    const careerPortalViewsData = Object.keys(careerPortalViews)
      .sort((a, b) => moment(a).format('YYYYMMDD') - moment(b).format('YYYYMMDD'))
      .map((date) => ({
        y: careerPortalViews[date].length,
        x: moment(date).format('MM/DD/YYYY'),
      }));
    setOverTimeData({
      datasets: [
        {
          label: 'New Candidates',
          data: selectedDates.map((date) => {
            return { x: date, y: selectedCandidates.filter(({ createdAt }) => createdAt === date).length };
          }),
          fill: false,
          backgroundColor: 'rgb(132, 120, 250)',
          borderColor: 'rgba(132, 120, 250, 0.4)',
        },
        {
          label: 'Job description page views',
          data: jobViewsData,
          fill: false,
          backgroundColor: 'rgb(12, 120, 250)',
          borderColor: 'rgba(12, 120, 250, 0.4)',
        },
        {
          label: 'Careers page views',
          data: careerPortalViewsData,
          fill: false,
          backgroundColor: 'rgb(200, 20, 50)',
          borderColor: 'rgba(200, 20, 50, 0.4)',
        },
      ],
      isEmpty: isEmpty(selectedCandidates),
    });
  };

  useEffect(() => {
    buildOverTimeData();
    buildDeviceData();
    buildSourceData();
    buildTagsData();
    buildDispositionCodeData();
  }, [candidates, startDate, endDate]);

  useEffect(() => {
    buildOverTimeData();
  }, [visits]);

  filterVisits();

  if (!loading) {
    return (
      <AppProvider>
        <Frame>
          <TitleBar title='Analytics' />
          <Page title='Analytics'>
            <Stack vertical>
              <Stack distribution={'fill'}>
                <Select label={'Date range'} options={dateOptions} onChange={handleDateRangeChange} value={dateRange} />
                <Stack.Item></Stack.Item>
                <Stack.Item></Stack.Item>
                <Stack.Item></Stack.Item>
              </Stack>

              <div className={'analytics__resume'}>
                <Card>
                  <Card.Section>
                    <div className='dashboard__resume-card'>
                      <span className='dashboard__resume-title'>{(careerPortalVisits() || []).length}</span>
                      <span>
                        <Link onClick={() => changeRoute(`/analytics/careers-page-views`)} removeUnderline>
                          Careers page views
                        </Link>
                      </span>
                    </div>
                  </Card.Section>
                </Card>
                <Card>
                  <Card.Section>
                    <div className='dashboard__resume-card'>
                      <span className='dashboard__resume-title'>{(jobDetailsVisits() || []).length}</span>
                      <span>
                        <Link onClick={changeRoute(`/analytics/job-description-views`) } removeUnderline>
                          Job description page views
                        </Link>
                      </span>
                    </div>
                  </Card.Section>
                </Card>
                <Card>
                  <Card.Section>
                    <div className='dashboard__resume-card'>
                      <span className='dashboard__resume-title'>{(filterCandidates() || []).length}</span>
                      <span>
                        <Link onClick={changeRoute(`/analytics/candidate-sourcing`) } removeUnderline>
                          New candidates
                        </Link>
                      </span>
                    </div>
                  </Card.Section>
                </Card>
                <Card>
                  <Card.Section>
                    <div className='dashboard__resume-card'>
                      <span className='dashboard__resume-title'>{funnelConversion()}%</span>
                      <span style={{ marginRight: '12%', marginLeft: '12%' }}>Application form conversion rate</span>
                    </div>
                  </Card.Section>
                </Card>
              </div>

              <div className={'analytics__resume'}>
                <Card>
                  <Card.Section>
                    <div className='dashboard__resume-card'>
                      {!isSubscribedUser ? (
                        <div style={{ marginBottom: '10px', flexDirection: 'row', justifyContent: 'center' }}>
                          <Stack spacing={'extraTight'} distribution={'center'}>
                            <Icon source={LockMinor} />
                            <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                              Upgrade account plan
                            </Link>
                          </Stack>
                        </div>
                      ) : (
                        <span className='dashboard__resume-title'>{(filterCandidatesNewsletters() || []).length}</span>
                      )}

                      <span style={{ marginRight: '12%', marginLeft: '12%' }}>Candidates subscribed to your newsletter</span>
                    </div>
                  </Card.Section>
                </Card>
                <Card>
                  <Card.Section>
                    <div className='dashboard__resume-card'>
                      {!isSubscribedUser ? (
                        <div style={{ marginBottom: '10px', flexDirection: 'row', justifyContent: 'center' }}>
                          <Stack spacing={'extraTight'} distribution={'center'}>
                            <Icon source={LockMinor} />
                            <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                              Upgrade account plan
                            </Link>
                          </Stack>
                        </div>
                      ) : (
                        <span className='dashboard__resume-title'>{filterCandidatesNewslettersPercentage()}%</span>
                      )}
                      <span style={{ marginRight: '12%', marginLeft: '12%' }}>Candidates subscribed to your newsletter</span>
                    </div>
                  </Card.Section>
                </Card>
                <Card>
                  <Card.Section>
                    <div className='dashboard__resume-card'>
                      <span className='dashboard__resume-title'>{(filterSubscribers() || []).length}</span>

                      <span>Job alert subscribers</span>
                    </div>
                  </Card.Section>
                </Card>
                <Card>
                  <Card.Section title={''}>
                    <div className='dashboard__resume-card'>
                      {!isSubscribedUser ? (
                        <div style={{ marginBottom: '10px', flexDirection: 'row', justifyContent: 'center' }}>
                          <Stack spacing={'extraTight'} distribution={'center'}>
                            <Icon source={LockMinor} />
                            <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                              Upgrade account plan
                            </Link>
                          </Stack>
                        </div>
                      ) : (
                        <span className='dashboard__resume-title'>{socialMediaClicks()}</span>
                      )}
                      <span>Clicks on your social profiles after applying</span>
                    </div>
                  </Card.Section>
                </Card>
              </div>

              <Card>
                <Card.Section title={'New candidates over time'}>
                  {(overTimeData || {}).isEmpty ? (
                    <ChartEmptyState term={'new candidates'} />
                  ) : (
                    <Line
                      data={overTimeData}
                      options={{
                        scales: {
                          yAxes: [
                            {
                              ticks: {
                                beginAtZero: true,
                              },
                            },
                          ],
                          xAxes: [
                            {
                              type: 'time',
                              distribution: 'series',
                              time: {
                                unit: 'day',
                              },
                            },
                          ],
                        },
                      }}
                    />
                  )}
                </Card.Section>
              </Card>
              <Stack distribution='fillEvenly'>
                <Card>
                  <Card.Section title={'Candidates by device'}>
                    {(deviceData || {}).isEmpty ? <ChartEmptyState term={'new candidates'} /> : <Pie data={deviceData} />}
                  </Card.Section>
                </Card>
                <Card>
                  <Card.Section title={'Candidates by source'}>
                    {(sourceData || {}).isEmpty ? <ChartEmptyState term={'new candidates'} /> : <Pie data={sourceData} />}
                  </Card.Section>
                </Card>
              </Stack>
              <Stack distribution='fillEvenly'>
                <Card>
                  <Card.Section title={'Disposition code totals'}>
                    {(dispositionCodesData || {}).isEmpty ? (
                      <ChartEmptyState term={'disqualified candidates'} />
                    ) : (
                      <Pie data={dispositionCodesData} />
                    )}
                  </Card.Section>
                </Card>
                <Card>
                  <Card.Section title={'Candidate tags'}>
                    {(tagsData || {}).isEmpty ? <ChartEmptyState term={'candidates tagged'} /> : <Pie data={tagsData} />}
                  </Card.Section>
                </Card>
              </Stack>
            </Stack>
          </Page>
          <Footer link='https://help.shophire.co/article/39-hiring-analytics-overview' name='Analytics' />
        </Frame>
      </AppProvider>
    );
  } else {
    return (
      <AppProvider>
        <Loader metrics={true} />
      </AppProvider>
    );
  }
};

export default Analytics;
